import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Input, Label } from 'reactstrap';

import apiOffers from 'api/offers';
import IPdfLayoutPosition from 'api/interfaces/offers/IPdfLayoutPosition';
import translations from 'services/translations/translations';
import { ADDITIONAL_OFFER } from 'services/enums/offers/offerTypes';
import { addMessage } from 'store/messages/actions';

import 'style/offers/PdfPreviewPanel.css';

const STORAGE_KEY = 'offer-pdf-preview-panel';

interface IPanelRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

const getDefaultRect = (): IPanelRect => {
  const width = Math.min(1100, window.innerWidth - 40);
  const height = Math.min(850, window.innerHeight - 40);

  return {
    height,
    left: Math.max(20, window.innerWidth - width - 20),
    top: 20,
    width,
  };
};

// The panel position is a per-user convenience, it falls back to the default when storage
// is not available
const loadRect = (): IPanelRect => {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored !== null) {
      const rect = JSON.parse(stored) as IPanelRect;
      return {
        height: Math.min(rect.height, window.innerHeight),
        left: Math.min(Math.max(0, rect.left), window.innerWidth - 100),
        top: Math.min(Math.max(0, rect.top), window.innerHeight - 50),
        width: Math.min(rect.width, window.innerWidth),
      };
    }
  } catch (error) {
    // Use the default position
  }

  return getDefaultRect();
};

const saveRect = (rect: IPanelRect) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(rect));
  } catch (error) {
    // The position is just not remembered
  }
};

const PdfPreviewPanel = ({
  highlightedProductId,
  locked,
  offerId,
  onClose,
  type,
}: {
  highlightedProductId?: number;
  locked: boolean;
  offerId: number;
  onClose: () => void;
  type: string;
}): JSX.Element => {
  const dispatch = useDispatch();
  const { t } = useTranslation([translations.offers, translations.products]);

  const panel = useRef<HTMLDivElement>(null);
  const [rect, setRect] = useState<IPanelRect>(loadRect);
  const [pdfUrl, setPdfUrl] = useState<string>();
  const [positions, setPositions] = useState<IPdfLayoutPosition[]>([]);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState<number | null>(null);
  const [dragging, setDragging] = useState(false);

  const isAdditionalOffer = type === ADDITIONAL_OFFER;

  const load = async () => {
    setLoading(true);

    try {
      const [pdfResponse, layout] = await Promise.all([
        isAdditionalOffer
          ? apiOffers.additionalOffers.pdfs.get(offerId)
          : apiOffers.pdfs.get(offerId),
        isAdditionalOffer
          ? apiOffers.additionalOffers.pdfLayout.get(offerId)
          : apiOffers.pdfLayout.get(offerId),
      ]);

      const blob = new Blob([pdfResponse.data], { type: 'application/pdf' });
      setPdfUrl((previous) => {
        if (previous !== undefined) {
          URL.revokeObjectURL(previous);
        }
        return URL.createObjectURL(blob);
      });
      setPositions(layout);
    } catch (error) {
      dispatch(addMessage(t(`${translations.offers}:pdfPreview.Load failed`)));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [offerId, isAdditionalOffer]);

  useEffect(
    () => () => {
      setPdfUrl((previous) => {
        if (previous !== undefined) {
          URL.revokeObjectURL(previous);
        }
        return undefined;
      });
    },
    []
  );

  // Remember the size when the user resizes the panel from its corner
  useEffect(() => {
    const element = panel.current;
    if (element === null || typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const observer = new ResizeObserver(() => {
      setRect((current) => {
        const next = {
          ...current,
          height: element.offsetHeight,
          width: element.offsetWidth,
        };
        saveRect(next);
        return next;
      });
    });

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const startDrag = (event: React.MouseEvent) => {
    if ((event.target as HTMLElement).closest('button') !== null) {
      return;
    }

    event.preventDefault();
    setDragging(true);
    const startX = event.clientX;
    const startY = event.clientY;
    const startRect = rect;

    const move = (moveEvent: MouseEvent) => {
      setRect({
        ...startRect,
        left: Math.min(
          Math.max(0, startRect.left + moveEvent.clientX - startX),
          window.innerWidth - 100
        ),
        top: Math.min(
          Math.max(0, startRect.top + moveEvent.clientY - startY),
          window.innerHeight - 50
        ),
      });
    };

    const stop = () => {
      document.removeEventListener('mousemove', move);
      document.removeEventListener('mouseup', stop);
      setDragging(false);
      setRect((current) => {
        saveRect(current);
        return current;
      });
    };

    document.addEventListener('mousemove', move);
    document.addEventListener('mouseup', stop);
  };

  const togglePageBreak = async (position: IPdfLayoutPosition) => {
    setSavingId(position.id);

    try {
      await apiOffers.products.patchPageBreak(
        position.id,
        !position.pageBreakBefore
      );
      await load();
    } catch (error) {
      dispatch(addMessage(t(`${translations.offers}:pdfPreview.Save failed`)));
    } finally {
      setSavingId(null);
    }
  };

  const groups = positions.reduce(
    (result: { name: string; positions: IPdfLayoutPosition[] }[], position) => {
      const last = result[result.length - 1];
      if (last !== undefined && last.name === position.group) {
        last.positions.push(position);
      } else {
        result.push({ name: position.group, positions: [position] });
      }
      return result;
    },
    []
  );

  return ReactDOM.createPortal(
    <div
      ref={panel}
      className={`pdf-preview-panel${dragging ? ' dragging' : ''}`}
      role="dialog"
      aria-label={t(`${translations.offers}:pdfPreview.Title`)}
      style={{
        height: rect.height,
        left: rect.left,
        top: rect.top,
        width: rect.width,
      }}
    >
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <div className="pdf-preview-header" onMouseDown={startDrag}>
        <span className="pdf-preview-title">
          {t(`${translations.offers}:pdfPreview.Title`)}
        </span>
        <span className="pdf-preview-actions">
          <button
            type="button"
            className="pdf-preview-button"
            onClick={load}
            disabled={loading}
          >
            {t(`${translations.offers}:pdfPreview.Refresh`)}
          </button>
          <button
            type="button"
            className="pdf-preview-button"
            onClick={onClose}
            aria-label={t(`${translations.offers}:Close`)}
            title={t(`${translations.offers}:Close`)}
          >
            ✕
          </button>
        </span>
      </div>
      <div className="pdf-preview-body">
        <div className="pdf-preview-positions">
          <p className="pdf-preview-info">
            {locked
              ? t(`${translations.offers}:pdfPreview.Locked info`)
              : t(`${translations.offers}:pdfPreview.Info`)}
          </p>
          {groups.map((group) => (
            <div key={group.name} className="pdf-preview-group">
              <div className="pdf-preview-group-name">
                {t(`${translations.products}:groups:${group.name}`)}
              </div>
              {group.positions.map((position) => (
                <div
                  key={position.id}
                  className={`pdf-preview-position${
                    position.id === highlightedProductId ? ' highlighted' : ''
                  }${position.pageBreakBefore ? ' page-break' : ''}`}
                >
                  {position.pageBreakBefore ? (
                    <div className="pdf-preview-page-break-line">
                      {t(`${translations.offers}:pdfPreview.New page`)}
                    </div>
                  ) : null}
                  <div className="pdf-preview-position-name">
                    <strong>{position.position}</strong> {position.name}
                    {position.imageCount > 0 ? (
                      <span className="pdf-preview-image-count">
                        {' '}
                        {t(`${translations.offers}:pdfPreview.Images`, {
                          count: position.imageCount,
                        })}
                      </span>
                    ) : null}
                  </div>
                  <Label check className="pdf-preview-checkbox">
                    <Input
                      type="checkbox"
                      checked={position.pageBreakBefore}
                      disabled={locked || savingId !== null || loading}
                      onChange={() => togglePageBreak(position)}
                    />{' '}
                    {t(`${translations.offers}:pdfPreview.Start on new page`)}
                  </Label>
                </div>
              ))}
            </div>
          ))}
        </div>
        <div className="pdf-preview-document">
          {loading ? (
            <div className="pdf-preview-loading">
              {t(`${translations.offers}:pdfPreview.Loading`)}
            </div>
          ) : null}
          {pdfUrl === undefined ? null : (
            <iframe
              src={`${pdfUrl}#view=FitH`}
              title={t(`${translations.offers}:pdfPreview.Title`)}
            />
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default PdfPreviewPanel;
